import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '@/common/utils/encryption';
import {
  Button,
  EmptyState,
  SkeletonText,
  DashboardSection,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
  DashboardTableWrapper,
} from '@/components';
import { DashboardPage, DashboardHeader } from '../../components/PremiumDesign';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { ReminderImportJobSummary } from '../components/ReminderImportJobSummary';
import { useReminderImportJob } from '../hooks';
import { getCompanyId } from '../utils';

export function ReminderImportJobPage() {
  const navigate = useNavigate();
  const { jobId: encodedJobId } = useParams();
  const jobId = decodeId(encodedJobId);
  const companyId = getCompanyId();

  const {
    data: job,
    isLoading,
    isError,
  } = useReminderImportJob(companyId, jobId);

  const showSkeleton = useDelayedSkeleton(isLoading && !job);

  if (isError) {
    return (
      <DashboardPage>
        <DashboardHeader title="Import Job">
          <Button variant="secondary" onClick={() => navigate('/reminders')}>
            Back to Reminders
          </Button>
        </DashboardHeader>
        <div className="rounded-[12px] bg-white border border-border-default">
          <EmptyState
            title="Import job not found"
            description="This job may have been removed or you may not have access."
            actionLabel="Back to Reminders"
            onAction={() => navigate('/reminders')}
          />
        </div>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage>
      <DashboardHeader title="Import Job">
        <Button variant="secondary" onClick={() => navigate('/reminders')}>
          Back to Reminders
        </Button>
      </DashboardHeader>

      {showSkeleton ? (
        <SkeletonText lines={10} />
      ) : (
        <DashboardSection className="gap-4">
          <ReminderImportJobSummary job={job} />

          {job?.error_log?.length > 0 && (
            <div className="rounded-[12px] bg-white border border-border-default overflow-hidden">
              <div className="px-4 py-3 border-b border-border-default">
                <h3 className="text-body font-bold text-text-primary">
                  Error Log
                </h3>
                <p className="text-caption text-text-secondary mt-0.5">
                  Rows that could not be processed
                </p>
              </div>
              <DashboardTableWrapper>
                <DashboardTable>
                  <DashboardTableHeader>
                    <th className="!py-3 px-4 text-left">Row</th>
                    <th className="!py-3 px-4 text-left">Errors</th>
                    <th className="!py-3 px-4 text-left">Data</th>
                  </DashboardTableHeader>
                  <DashboardTableBody>
                    {job.error_log.map((entry, idx) => (
                      <DashboardTableRow key={idx} className="!cursor-default">
                        <DashboardTableCell>{entry.row}</DashboardTableCell>
                        <DashboardTableCell className="text-status-error">
                          {(entry.errors || []).join(', ')}
                        </DashboardTableCell>
                        <DashboardTableCell className="text-xs text-text-muted max-w-[280px] truncate">
                          {JSON.stringify(entry.data || {})}
                        </DashboardTableCell>
                      </DashboardTableRow>
                    ))}
                  </DashboardTableBody>
                </DashboardTable>
              </DashboardTableWrapper>
            </div>
          )}
        </DashboardSection>
      )}
    </DashboardPage>
  );
}
